"use client";

import { ReactNode, useEffect, useRef, useState } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { usePathname, useRouter } from "next/navigation";
import { FeatureFlagsProvider, useFeatureFlags, FEATURE_FLAGS } from "@/hooks/useFeatureFlags";
import { ToastProvider } from "@/components/ui/Toast";
import { apiFetchAuthed } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import { shouldSkipNewOnboardingFlow } from "@/lib/skip-new-onboarding-flow";
import { clearGuestCheckoutStorage } from "@/lib/guest-checkout-storage";

type ProvidersProps = {
  children: ReactNode;
};

/** Paths where unonboarded users are allowed (no redirect) */
const ONBOARDING_EXEMPT_PREFIXES = [
  "/onboarding",
  "/auth",
  "/invite",
  "/guest-ticket",
  "/legal",
  "/api",
];

function isExemptPath(pathname: string | null): boolean {
  if (!pathname) return true;
  return ONBOARDING_EXEMPT_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Wipes guest-checkout sessionStorage whenever the Clerk identity changes
 * (sign-out or account switch). Without this, a checkout abandoned at the
 * Razorpay step leaves `hopp_guest_info` behind; after logout the next
 * purchase on this tab silently reuses the previous user's verified phone,
 * name, and 30-minute checkout sessionToken — the buyer cannot switch
 * accounts and the ticket is attributed to the wrong person.
 *
 * Pure guests who never sign in are unaffected: their state only clears on
 * payment success, session expiry, or tab close, which is the intended UX.
 */
function GuestCheckoutCleanup() {
  const { isLoaded, userId } = useAuth();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!isLoaded) return;
    const prev = prevUserIdRef.current;
    const current = userId ?? null;
    // Only clear on a transition AWAY from a signed-in user. The initial
    // mount (prev === undefined) and guest→signed-in transitions keep the
    // in-flight checkout intact.
    if (typeof prev === "string" && prev !== current) {
      clearGuestCheckoutStorage();
    }
    prevUserIdRef.current = current;
  }, [isLoaded, userId]);

  return null;
}

function OnboardingGuard({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId, getToken } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const exempt = isExemptPath(pathname);
  const skipOnboarding = shouldSkipNewOnboardingFlow(
    isEnabled(FEATURE_FLAGS.SKIP_NEW_ONBOARDING_FLOW),
  );

  const { data: status } = useQuery({
    queryKey: queryKeys.onboarding.guard(userId!),
    queryFn: () =>
      apiFetchAuthed<{ hasUserProfile: boolean; hasHostProfile: boolean }>(
        "onboarding/status",
        {},
        () => getToken(),
      ),
    enabled: isLoaded && !!isSignedIn && !!userId && !exempt,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId || exempt) return;
    // Wait for feature flags to resolve — isEnabled() returns false during loading,
    // which would cause a premature redirect to /onboarding before the skip flag is known.
    if (flagsLoading) return;
    if (skipOnboarding) return;
    if (status && !status.hasUserProfile) {
      router.replace("/onboarding");
    }
  }, [isLoaded, isSignedIn, userId, status, exempt, flagsLoading, skipOnboarding, router]);

  return <>{children}</>;
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagsProvider>
        <ToastProvider>
          <GuestCheckoutCleanup />
          <OnboardingGuard>{children}</OnboardingGuard>
        </ToastProvider>
      </FeatureFlagsProvider>
    </QueryClientProvider>
  );
}
